import React, { useState, useEffect } from 'react';
import { adminAPI, productsAPI, categoriesAPI, enquiriesAPI, usersAPI, certificatesAPI, authAPI, getImageUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const Admin = () => {
  const { logout, user } = useAuth();

  // Use environment variable, fallback to localhost for local dev only if env is missing
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [certificates, setCertificates] = useState([]); // New state for certificates
  const [showProductForm, setShowProductForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showCertForm, setShowCertForm] = useState(false); // New form state for certificates
  const [showUserForm, setShowUserForm] = useState(false); // New form state for users
  const [showEnquiryModal, setShowEnquiryModal] = useState(false); // For viewing enquiry detail
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Admin Product Pagination & Search State
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [debouncedAdminSearch, setDebouncedAdminSearch] = useState('');
  const [adminSelectedCategory, setAdminSelectedCategory] = useState('all');
  const ADMIN_PRODUCT_LIMIT = 10;

  // Admin Category Pagination & Search State
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryTotalPages, setCategoryTotalPages] = useState(1);
  const [categoryTotalCount, setCategoryTotalCount] = useState(0);
  const [adminCategorySearchTerm, setAdminCategorySearchTerm] = useState('');
  const [debouncedAdminCategorySearch, setDebouncedAdminCategorySearch] = useState('');
  const ADMIN_CATEGORY_LIMIT = 10;

  // Form States
  const [productForm, setProductForm] = useState({
    name: '',
    sku: '',
    category: '',
    price: '',
    description: '',
    status: 'published',
    featured: false,
    brand: '',
    model: '',
    ports: '',
    printWidth: '',
    warranty: '',
    image: '',
    keyFeatures: ['']
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', iconClass: '', description: '', parentCategory: '', image: '' });
  const [certForm, setCertForm] = useState({ title: '', image: '' }); // New state for certificate form
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'viewer' }); // New state for user form
  const [parentCategorySelection, setParentCategorySelection] = useState('');

  const handleProductChange = (e) => setProductForm({ ...productForm, [e.target.name]: e.target.value });

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      const generatedSlug = value.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
      setCategoryForm({ ...categoryForm, name: value, slug: generatedSlug });
    } else {
      setCategoryForm({ ...categoryForm, [name]: value });
    }
  };

  const handleProductEdit = (product) => {
    // Determine parent category for the dropdowns
    const currentCatId = product.category?._id || product.category;
    const currentCat = categories.find(c => c._id === currentCatId);
    if (currentCat && currentCat.parentCategory) {
      setParentCategorySelection(currentCat.parentCategory._id || currentCat.parentCategory);
    } else {
      setParentCategorySelection(currentCatId || '');
    }

    setProductForm({
      name: product.name,
      sku: product.sku || '',
      category: currentCatId || '',
      price: product.price || '',
      description: product.fullDescription || product.shortDescription || '',
      status: product.status || 'published',
      featured: product.featured || false,
      brand: product.brand || '',
      model: product.model || '',
      ports: product.ports || '',
      printWidth: product.printWidth || '',
      warranty: product.warranty || '',
      image: product.images && product.images.length > 0 ? product.images[0] : '',
      existingImages: product.images || [], // Store existing images for preservation
      keyFeatures: product.keyFeatures && product.keyFeatures.length > 0 ? product.keyFeatures : ['']
    });
    setEditingId(product._id);
    setShowProductForm(true);
  };

  const handleProductDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setLoading(true);
      try {
        await productsAPI.delete(id);
        const response = await productsAPI.getAll();
        setProducts(Array.isArray(response.data) ? response.data : (response.data.products || []));
      } catch (error) {
        console.error("Failed to delete product", error);
        alert("Failed to delete product");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCategoryEdit = (category) => {
    setCategoryForm({
      name: category.name,
      slug: category.slug || '',
      iconClass: category.iconClass || '',
      description: category.description || '',
      image: category.image || ''
    });
    setEditingId(category._id);
    setShowCategoryForm(true);
  };

  const handleCategoryDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      setLoading(true);
      try {
        await categoriesAPI.delete(id);
        const response = await categoriesAPI.getAll();
        setCategories(response.data.categories || response.data);
      } catch (error) {
        console.error("Failed to delete category", error);
        alert("Failed to delete category");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await categoriesAPI.update(editingId, categoryForm);
      } else {
        await categoriesAPI.create(categoryForm);
      }
      setShowCategoryForm(false);
      setEditingId(null);
      setCategoryForm({ name: '', slug: '', iconClass: '', description: '', parentCategory: '', image: '' });
      // Refresh categories
      const response = await categoriesAPI.getAll();
      setCategories(response.data.categories || response.data);
    } catch (error) {
      console.error("Failed to save category", error);
      alert("Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // When editing, preserve existing images if no new image is provided
      let imagesToSave = [];
      if (productForm.image) {
        imagesToSave = [productForm.image];
      } else if (editingId && productForm.existingImages && productForm.existingImages.length > 0) {
        imagesToSave = productForm.existingImages; // Preserve existing when editing
      }

      const payload = {
        ...productForm,
        fullDescription: productForm.description,
        shortDescription: productForm.description.substring(0, 150),
        images: imagesToSave,
        keyFeatures: productForm.keyFeatures.filter(f => f.trim() !== '')
      };

      // Debug logging
      console.log('🔄 Submitting product update:', {
        editingId,
        featured: payload.featured,
        images: payload.images,
        payload
      });
      if (editingId) {
        await productsAPI.update(editingId, payload);
      } else {
        await productsAPI.create(payload);
      }
      setShowProductForm(false);
      setEditingId(null);
      setProductForm({
        name: '',
        sku: '',
        category: '',
        price: '',
        description: '',
        status: 'published',
        featured: false,
        brand: '',
        model: '',
        ports: '',
        printWidth: '',
        warranty: ''
      });
      // Refresh products
      const response = await productsAPI.getAll();
      setProducts(Array.isArray(response.data) ? response.data : (response.data.products || []));
    } catch (error) {
      console.error("Failed to create product", error);
      alert("Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.register(userForm);
      setShowUserForm(false);
      setUserForm({ name: '', email: '', password: '', role: 'viewer' });
      // Refresh users
      const response = await usersAPI.getAll();
      setUsers(response.data);
      alert("User created successfully");
    } catch (error) {
      console.error("Failed to create user", error);
      alert(error.response?.data?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (id, newRole) => {
    if (window.confirm(`Change user role to ${newRole}?`)) {
      setLoading(true);
      try {
        await usersAPI.updateRole(id, newRole);
        const response = await usersAPI.getAll();
        setUsers(response.data);
      } catch (error) {
        console.error("Failed to update role", error);
        alert("Failed to update role");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUserDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setLoading(true);
      try {
        await usersAPI.delete(id);
        const response = await usersAPI.getAll();
        setUsers(response.data);
      } catch (error) {
        console.error("Failed to delete user", error);
        alert(error.response?.data?.message || "Failed to delete user");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEnquiryView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowEnquiryModal(true);
  };

  // Certificate Handlers
  const handleCertEdit = (cert) => {
    setCertForm({
      title: cert.title,
      image: cert.image
    });
    setEditingId(cert._id);
    setShowCertForm(true);
  };

  const handleCertDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      setLoading(true);
      try {
        await certificatesAPI.delete(id);
        const response = await certificatesAPI.getAll();
        setCertificates(response.data);
      } catch (error) {
        console.error("Failed to delete certificate", error);
        alert("Failed to delete certificate");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCertSubmit = async (e) => {
    e.preventDefault();
    if (!certForm.title || (!certForm.image && !editingId)) {
      alert("Title and Image are required");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        await certificatesAPI.update(editingId, certForm);
      } else {
        await certificatesAPI.create(certForm);
      }
      setShowCertForm(false);
      setEditingId(null);
      setCertForm({ title: '', image: '' });
      // Refresh
      const response = await certificatesAPI.getAll();
      setCertificates(response.data);
    } catch (error) {
      console.error("Failed to save certificate", error);
      alert("Failed to save certificate");
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    const password = window.prompt("To clear all products and categories, enter the clearance password:");
    if (!password) return;

    if (window.confirm("WARNING: This will permanently delete ALL products and categories. Are you 100% sure?")) {
      setLoading(true);
      try {
        const response = await adminAPI.clearData(password);
        alert(response.data.message);
        // Refresh everything
        fetchProducts();
        fetchCategories();
        // Reset active section to dashboard to see updated stats
        setActiveSection('dashboard');
      } catch (error) {
        console.error("Failed to clear data", error);
        alert(error.response?.data?.message || "Failed to clear data");
      } finally {
        setLoading(false);
      }
    }
  };

  // Stats Effect
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    if (activeSection === 'dashboard') {
      fetchStats();
    }
  }, [activeSection]);

  const fetchProducts = async (pageToFetch = 1, search = '', category = 'all') => {
    setLoading(true);
    try {
      const params = {
        page: pageToFetch,
        limit: ADMIN_PRODUCT_LIMIT,
        search: search,
        _t: Date.now()
      };

      if (category && category !== 'all') {
        params.category = category;
      }

      const response = await productsAPI.getAll(params);
      const { products: fetchedProducts, totalCount, totalPages } = response.data;
      setProducts(fetchedProducts || []);
      setProductTotalCount(totalCount || 0);
      setProductTotalPages(totalPages || 1);
      setProductPage(pageToFetch);
    } catch (error) {
      console.error("Failed to fetch products", error);
      // Clear products on error to avoid showing stale data from previous filter
      setProducts([]);
      setProductTotalCount(0);
      setProductTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (pageToFetch = 1, search = '', all = false) => {
    try {
      const params = {
        _t: Date.now()
      };

      if (all) {
        params.all = 'true';
      } else {
        params.page = pageToFetch;
        params.limit = ADMIN_CATEGORY_LIMIT;
        params.search = search;
      }

      const response = await categoriesAPI.getAll(params);

      if (all) {
        setCategories(response.data.categories || response.data);
      } else {
        const { categories: fetchedCategories, totalCount, totalPages } = response.data;
        setCategories(fetchedCategories || []);
        setCategoryTotalCount(totalCount || 0);
        setCategoryTotalPages(totalPages || 1);
        setCategoryPage(pageToFetch);
      }
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  // Sections Data Fetching Effect
  useEffect(() => {
    if (activeSection === 'products') {
      fetchCategories(1, '', true); // Fetch ALL categories for the dropdown
    }

    if (activeSection === 'categories' || activeSection === 'subcategories') {
      setLoading(true);
      fetchCategories(1, debouncedAdminCategorySearch).finally(() => setLoading(false));
    }

    if (activeSection === 'enquiries') {
      const fetchEnquiries = async () => {
        setLoading(true);
        try {
          // Assuming enquiriesAPI.getAll() exists
          const response = await enquiriesAPI.getAll();
          // Response might be array directly or { data: ... }
          setEnquiries(response.data || response);
        } catch (error) {
          console.error("Failed to fetch enquiries", error);
        } finally {
          setLoading(false);
        }
      };
      fetchEnquiries();
    }

    if (activeSection === 'users') {
      const fetchUsers = async () => {
        setLoading(true);
        try {
          const response = await usersAPI.getAll();
          setUsers(response.data);
        } catch (error) {
          console.error("Failed to fetch users", error);
        } finally {
          setLoading(false);
        }
      };
      fetchUsers();
    }

    if (activeSection === 'certifications') {
      const fetchCertificates = async () => {
        setLoading(true);
        try {
          const response = await certificatesAPI.getAll();
          setCertificates(response.data);
        } catch (error) {
          console.error("Failed to fetch certificates", error);
        } finally {
          setLoading(false);
        }
      };
      fetchCertificates();
    }
  }, [activeSection]);

  // Effect to refetch products when search or category changes, or when entering products section
  useEffect(() => {
    if (activeSection === 'products') {
      fetchProducts(1, debouncedAdminSearch, adminSelectedCategory);
    }
  }, [debouncedAdminSearch, adminSelectedCategory, activeSection]);

  // Search Debounce Logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminSearch(adminSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [adminSearchTerm]);

  // Handle Page Change
  const handleAdminPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= productTotalPages) {
      fetchProducts(newPage, debouncedAdminSearch, adminSelectedCategory);
    }
  };

  // Category Search Debounce Logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminCategorySearch(adminCategorySearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [adminCategorySearchTerm]);

  // Effect to refetch categories when category search changes
  useEffect(() => {
    if (activeSection === 'categories') {
      fetchCategories(1, debouncedAdminCategorySearch);
    }
  }, [debouncedAdminCategorySearch, activeSection]);

  // Handle Category Page Change
  const handleAdminCategoryPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= categoryTotalPages) {
      fetchCategories(newPage, debouncedAdminCategorySearch);
    }
  };

  const showSection = (section) => {
    setActiveSection(section);
    setShowCategoryForm(false);
    setEditingId(null);
    setProductForm({ name: '', sku: '', category: '', price: '', description: '', status: 'published', featured: false, brand: '', model: '', ports: '', printWidth: '', warranty: '', image: '', keyFeatures: [''] });
    setCategoryForm({ name: '', slug: '', iconClass: '', description: '', parentCategory: '', image: '' });
    setCertForm({ title: '', image: '' });
    setShowCertForm(false);
    setParentCategorySelection('');
    setSidebarOpen(false);
  };

  return (
    <div className="flex bg-gray-100 dark:bg-gray-900" style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-gray-900 text-white flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <i className="fas fa-shield-alt text-xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-lg tracking-wide">Admin</h2>
              <p className="text-xs text-gray-400">Control Panel</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-400 hover:text-white">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {[
            { id: 'dashboard', icon: 'chart-line', label: 'Dashboard' },
            { id: 'products', icon: 'box', label: 'Products' },
            { id: 'certifications', icon: 'certificate', label: 'Certifications' },
            { id: 'categories', icon: 'folder', label: 'Categories' },
            { id: 'subcategories', icon: 'folder-open', label: 'Sub Categories' },
            { id: 'enquiries', icon: 'envelope', label: 'Enquiries' },
            { id: 'users', icon: 'users', label: 'Users' }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => showSection(item.id)}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${activeSection === item.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
            >
              <i className={`fas fa-${item.icon} w-5 text-center mr-3`}></i>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center px-4 py-3 mb-2">
            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold mr-3">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <i className="fas fa-sign-out-alt mr-2"></i>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-700 mr-4">
                <i className="fas fa-bars text-xl"></i>
              </button>
              <h1 className="text-2xl font-bold text-gray-900 capitalize">
                {activeSection === 'products' && showProductForm ? (editingId ? 'Edit Product' : 'Add New Product') :
                  activeSection === 'categories' && showCategoryForm ? (editingId ? 'Edit Category' : 'Add New Category') :
                    activeSection === 'subcategories' && showCategoryForm ? (editingId ? 'Edit Sub Category' : 'Add New Sub Category') :
                      activeSection === 'certifications' && showCertForm ? (editingId ? 'Edit Certificate' : 'Add New Certificate') :
                        activeSection === 'subcategories' ? 'Sub Categories Master' :
                          activeSection}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {activeSection === 'dashboard' && (
                <button
                  onClick={handleClearData}
                  className="bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center border border-red-200"
                >
                  <i className="fas fa-trash-alt mr-2"></i> Clear All Data
                </button>
              )}
              <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                View Website <i className="fas fa-external-link-alt ml-1"></i>
              </a>
            </div>
          </div>
        </header>

        {/* Content Body */}
        <div className="flex-1 overflow-auto p-6 lg:p-8">

          {/* DASHBOARD VIEW */}
          {activeSection === 'dashboard' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Total Products</p>
                    <h3 className="text-3xl font-bold text-gray-900 mt-1">{stats.stats?.totalProducts || 0}</h3>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <i className="fas fa-box text-blue-600 text-xl"></i>
                  </div>
                </div>
              </div>
              {/* Additional stats placeholders would go here, mimicking the original design but cleaner */}
            </div>
          )}

          {/* PRODUCTS VIEW */}
          {activeSection === 'products' && !showProductForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Product List</h3>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="relative">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="Search name, SKU, brand..."
                      value={adminSearchTerm}
                      onChange={(e) => setAdminSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-48"
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={adminSelectedCategory}
                      onChange={(e) => {
                        setAdminSelectedCategory(e.target.value);
                      }}
                      className="pl-3 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-48 appearance-none cursor-pointer"
                    >
                      <option value="all">All Categories</option>
                      {categories.filter(c => !c.parentCategory).map(cat => (
                        <React.Fragment key={cat._id}>
                          <option value={cat._id} className="font-bold">{cat.name}</option>
                          {categories
                            .filter(sub => sub.parentCategory && (sub.parentCategory._id === cat._id || sub.parentCategory === cat._id))
                            .map(sub => (
                              <option key={sub._id} value={sub._id}>&nbsp;&nbsp;&nbsp;{sub.name}</option>
                            ))
                          }
                        </React.Fragment>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      id="excel-upload"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const formData = new FormData();
                        formData.append('file', file);

                        setLoading(true);
                        try {
                          const res = await fetch(`${API_BASE}/upload/products`, {
                            method: 'POST',
                            body: formData
                          });
                          const data = await res.json();
                          if (res.ok) {
                            alert(`Upload Complete!\n\nTotal Rows: ${data.results.total}\nNew Products: ${data.results.created}\nUpdated Products: ${data.results.updated}\nErrors: ${data.results.errors.length}`);
                            if (data.results.errors.length > 0) {
                              console.error("Upload Errors:", data.results.errors);
                              alert("Check console for error details.");
                            }
                            // Refresh products and categories using new fetching logic
                            setAdminSearchTerm('');
                            fetchProducts(1, '');
                            fetchCategories();
                          } else {
                            alert(data.message || "Upload failed");
                          }
                        } catch (err) {
                          console.error("Upload failed", err);
                          alert("Upload failed. Make sure server is running.");
                        } finally {
                          setLoading(false);
                          e.target.value = '';
                        }
                      }}
                    />
                    <label
                      htmlFor="excel-upload"
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center cursor-pointer"
                    >
                      <i className="fas fa-file-excel mr-2"></i> Import
                    </label>
                    <button
                      onClick={() => setShowProductForm(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                    >
                      <i className="fas fa-plus mr-2"></i> Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Product Count Summary */}
              <div className="px-6 py-3 bg-blue-50/50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                  {productTotalCount} Total Products Found
                </div>
                {productTotalCount > 0 && (
                  <div className="text-xs text-gray-500">
                    Showing {((productPage - 1) * ADMIN_PRODUCT_LIMIT) + 1} to {Math.min(productPage * ADMIN_PRODUCT_LIMIT, productTotalCount)}
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr><td colSpan="4" className="text-center py-4">Loading...</td></tr>
                    ) : products.length === 0 ? (
                      <tr><td colSpan="4" className="text-center py-8 text-gray-500">No products found.</td></tr>
                    ) : products.map(product => (
                      <tr key={product._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.slug}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.category?.parentCategory ? (
                            <span>
                              <span className="text-gray-400">{product.category.parentCategory.name} &gt; </span>
                              {product.category.name}
                            </span>
                          ) : (
                            product.category?.name || 'Uncategorized'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {product.status || 'Draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => handleProductEdit(product)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                          <button onClick={() => handleProductDelete(product._id)} className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {productTotalPages > 1 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleAdminPageChange(productPage - 1)}
                    disabled={productPage === 1}
                    className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="fas fa-chevron-left text-xs"></i>
                  </button>

                  {[...Array(productTotalPages)].map((_, idx) => {
                    const p = idx + 1;
                    if (p === 1 || p === productTotalPages || (p >= productPage - 1 && p <= productPage + 1)) {
                      return (
                        <button
                          key={p}
                          onClick={() => handleAdminPageChange(p)}
                          className={`w-8 h-8 flex items-center justify-center rounded border text-xs font-bold transition-colors ${productPage === p
                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {p}
                        </button>
                      );
                    } else if (p === productPage - 2 || p === productPage + 2) {
                      return <span key={p} className="text-gray-400 font-bold px-1">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => handleAdminPageChange(productPage + 1)}
                    disabled={productPage === productTotalPages}
                    className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="fas fa-chevron-right text-xs"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ADD PRODUCT FORM */}
          {activeSection === 'products' && showProductForm && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 max-w-3xl mx-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Product Details</h3>
                <button onClick={() => setShowProductForm(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              <form onSubmit={handleProductSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Product Name</label>
                    <input name="name" value={productForm.name} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="e.g. Industrial Printer X1" required />
                  </div>



                  {/* Category Selection Split */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Category</label>
                      <select
                        value={parentCategorySelection}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          setParentCategorySelection(newVal);
                          setProductForm({ ...productForm, category: newVal }); // Default to parent
                        }}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.filter(c => !c.parentCategory).map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Sub Category (Optional)</label>
                      <select
                        name="category"
                        value={productForm.category}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleProductChange(e);
                          } else {
                            // If they select "Select Sub Category" (reset), set back to parent
                            setProductForm({ ...productForm, category: parentCategorySelection });
                          }
                        }}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                        disabled={!parentCategorySelection}
                      >
                        <option value="">Select Sub Category</option>
                        {categories
                          .filter(c => c.parentCategory && (c.parentCategory._id === parentCategorySelection || c.parentCategory === parentCategorySelection))
                          .map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>


                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Product Image</label>
                      <div className="flex items-center space-x-4">
                        {productForm.image && (
                          <img src={getImageUrl(productForm.image)} alt="Preview" className="h-12 w-12 object-cover rounded shadow-sm" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await fetch(`${API_BASE}/upload/images`, {
                                  method: 'POST',
                                  body: formData
                                });
                                const data = await res.json();
                                if (data.url) {
                                  setProductForm({ ...productForm, image: data.url });
                                }
                              } catch (err) {
                                console.error("Upload failed", err);
                                alert("Image upload failed");
                              }
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                      <select name="status" value={productForm.status} onChange={handleProductChange} className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border">
                        <option value="published">Published</option>
                        <option value="draft">Draft</option>
                        <option value="out_of_stock">Out of Stock</option>
                      </select>
                    </div>
                  </div>



                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Brand</label>
                      <input name="brand" value={productForm.brand} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="Brand Name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Model</label>
                      <input name="model" value={productForm.model} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="Model Number" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Ports</label>
                      <input name="ports" value={productForm.ports} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="e.g. USB, Ethernet" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Print Width</label>
                      <input name="printWidth" value={productForm.printWidth} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="e.g. 4 inches" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Warranty</label>
                      <input name="warranty" value={productForm.warranty} onChange={handleProductChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="e.g. 1 Year" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                    <textarea name="description" value={productForm.description} onChange={handleProductChange} rows="4" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Key Features</label>
                    <div className="space-y-2">
                      {productForm.keyFeatures.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => {
                              const newFeatures = [...productForm.keyFeatures];
                              newFeatures[index] = e.target.value;
                              setProductForm({ ...productForm, keyFeatures: newFeatures });
                            }}
                            className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2 px-4 border"
                            placeholder="Enter feature"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newFeatures = productForm.keyFeatures.filter((_, i) => i !== index);
                              setProductForm({ ...productForm, keyFeatures: newFeatures });
                            }}
                            className="text-red-500 hover:text-red-700 px-2"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setProductForm({ ...productForm, keyFeatures: [...productForm.keyFeatures, ''] })}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        + Add Feature
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="featured"
                      name="featured"
                      type="checkbox"
                      checked={productForm.featured}
                      onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="featured" className="ml-2 block text-sm text-gray-900 font-medium">
                      Feature this product (Show in Slider & Home)
                    </label>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-100">
                    <button type="button" onClick={() => setShowProductForm(false)} className="mr-3 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
                    <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm">
                      {loading ? 'Saving...' : 'Save Product'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )
          }

          {
            activeSection === 'categories' && !showCategoryForm && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">Categories List</h3>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="relative">
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        placeholder="Search categories..."
                        value={adminCategorySearchTerm}
                        onChange={(e) => setAdminCategorySearchTerm(e.target.value)}
                        className="pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                      />
                    </div>
                    <button
                      onClick={() => setShowCategoryForm(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                    >
                      <i className="fas fa-plus mr-2"></i> Add
                    </button>
                  </div>
                </div>

                {/* Category Count Summary */}
                <div className="px-6 py-3 bg-blue-50/50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
                    {categoryTotalCount} Total Categories Found
                  </div>
                  {categoryTotalCount > 0 && (
                    <div className="text-xs text-gray-500">
                      Showing {((categoryPage - 1) * ADMIN_CATEGORY_LIMIT) + 1} to {Math.min(categoryPage * ADMIN_CATEGORY_LIMIT, categoryTotalCount)}
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Icon</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr><td colSpan="4" className="text-center py-4">Loading...</td></tr>
                      ) : categories.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-8 text-gray-500">No categories found.</td></tr>
                      ) : categories.map(category => (
                        <tr key={category._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <img src={getImageUrl(category.image)} alt={category.name} className="h-10 w-10 object-contain rounded bg-gray-50 border border-gray-200" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                            {category.parentCategory && (
                              <div className="text-xs text-gray-400">Subcategory of {category.parentCategory.name}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {category.slug}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <i className={`${category.iconClass} mr-2`}></i> {category.iconClass}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button onClick={() => handleCategoryEdit(category)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button onClick={() => handleCategoryDelete(category._id)} className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {categoryTotalPages > 1 && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleAdminCategoryPageChange(categoryPage - 1)}
                      disabled={categoryPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-chevron-left text-xs"></i>
                    </button>

                    {[...Array(categoryTotalPages)].map((_, idx) => {
                      const p = idx + 1;
                      if (p === 1 || p === categoryTotalPages || (p >= categoryPage - 1 && p <= categoryPage + 1)) {
                        return (
                          <button
                            key={p}
                            onClick={() => handleAdminCategoryPageChange(p)}
                            className={`w-8 h-8 flex items-center justify-center rounded border text-xs font-bold transition-colors ${categoryPage === p
                              ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'
                              }`}
                          >
                            {p}
                          </button>
                        );
                      } else if (p === categoryPage - 2 || p === categoryPage + 2) {
                        return <span key={p} className="text-gray-400 font-bold px-1">...</span>;
                      }
                      return null;
                    })}

                    <button
                      onClick={() => handleAdminCategoryPageChange(categoryPage + 1)}
                      disabled={categoryPage === categoryTotalPages}
                      className="w-8 h-8 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="fas fa-chevron-right text-xs"></i>
                    </button>
                  </div>
                )}
              </div>
            )
          }

          {/* CERTIFICATIONS VIEW */}
          {activeSection === 'certifications' && !showCertForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Certifications List</h3>
                <button
                  onClick={() => setShowCertForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                >
                  <i className="fas fa-plus mr-2"></i> Add Certificate
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr><td colSpan="4" className="text-center py-4">Loading...</td></tr>
                    ) : certificates.length === 0 ? (
                      <tr><td colSpan="4" className="text-center py-8 text-gray-500">No certificates found.</td></tr>
                    ) : certificates.map(cert => (
                      <tr key={cert._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <img src={getImageUrl(cert.image)} alt={cert.title} className="h-10 w-10 object-contain rounded bg-gray-50 border border-gray-200" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{cert.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(cert.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => handleCertEdit(cert)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                          <button onClick={() => handleCertDelete(cert._id)} className="text-red-600 hover:text-red-900">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CERTIFICATE FORM */}
          {activeSection === 'certifications' && showCertForm && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Certificate Details</h3>
                <button onClick={() => setShowCertForm(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              <form onSubmit={handleCertSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={certForm.title}
                    onChange={(e) => setCertForm({ ...certForm, title: e.target.value })}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Certificate Image</label>
                  <div className="flex items-center space-x-4">
                    {certForm.image && (
                      <img src={getImageUrl(certForm.image)} alt="Preview" className="h-20 w-20 object-contain rounded bg-gray-50 border border-gray-200" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const res = await fetch(`${API_BASE}/upload/images`, {
                              method: 'POST',
                              body: formData
                            });
                            const data = await res.json();
                            if (data.url) {
                              setCertForm({ ...certForm, image: data.url });
                            }
                          } catch (err) {
                            console.error("Upload failed", err);
                            alert("Image upload failed");
                          }
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setShowCertForm(false)} className="mr-3 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm">
                    {loading ? 'Saving...' : 'Save Certificate'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* SUB-CATEGORIES VIEW */}
          {
            activeSection === 'subcategories' && !showCategoryForm && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">Sub-Categories List</h3>
                  <button
                    onClick={() => {
                      setCategoryForm({ name: '', slug: '', iconClass: '', description: '', parentCategory: '' });
                      setShowCategoryForm(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                  >
                    <i className="fas fa-plus mr-2"></i> Add Sub Category
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sub Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr><td colSpan="4" className="text-center py-4">Loading...</td></tr>
                      ) : categories.filter(c => c.parentCategory).length === 0 ? (
                        <tr><td colSpan="4" className="text-center py-8 text-gray-500">No sub-categories found.</td></tr>
                      ) : categories.filter(c => c.parentCategory).map(category => {
                        const parent = categories.find(p => p._id === (category.parentCategory._id || category.parentCategory));
                        return (
                          <tr key={category._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <img src={getImageUrl(category.image)} alt={category.name} className="h-10 w-10 object-contain rounded bg-gray-50 border border-gray-200" />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{category.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <span className="bg-gray-100 text-gray-800 py-1 px-2 rounded-full text-xs font-medium">
                                {parent ? parent.name : 'Unknown Parent'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {category.slug}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button onClick={() => handleCategoryEdit(category)} className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                              <button onClick={() => handleCategoryDelete(category._id)} className="text-red-600 hover:text-red-900">Delete</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          }

          {/* ADD CATEGORY/SUB-CATEGORY FORM */}
          {
            (activeSection === 'categories' || activeSection === 'subcategories') && showCategoryForm && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 max-w-3xl mx-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-900">
                    {activeSection === 'subcategories' ? 'Sub Category Details' : 'Category Details'}
                  </h3>
                  <button onClick={() => setShowCategoryForm(false)} className="text-gray-400 hover:text-gray-600">
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
                <form onSubmit={handleCategorySubmit} className="p-6 space-y-6">
                  <div className="grid grid-cols-1 gap-6">
                    {activeSection === 'subcategories' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Select Category
                        </label>
                        <select
                          name="parentCategory"
                          value={categoryForm.parentCategory}
                          onChange={handleCategoryChange}
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border"
                          required
                        >
                          <option value="">Select Category</option>
                          {categories
                            .filter(c => !c.parentCategory && c._id !== editingId) // Only show top-level categories as parents
                            .map(cat => (
                              <option key={cat._id} value={cat._id}>{cat.name}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Select the main category this belongs to.</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {activeSection === 'subcategories' ? 'Sub Category Name' : 'Category Name'}
                      </label>
                      <input name="name" value={categoryForm.name} onChange={handleCategoryChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder={activeSection === 'subcategories' ? "e.g. Industrial" : "e.g. Thermal Printer"} required />
                    </div>
                    {activeSection !== 'subcategories' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Slug (Auto-generated/Editable)</label>
                          <input name="slug" value={categoryForm.slug} onChange={handleCategoryChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm bg-gray-50 py-2.5 px-4 border text-gray-500" placeholder="thermal-printer" />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Icon Class</label>
                          <input name="iconClass" value={categoryForm.iconClass} onChange={handleCategoryChange} type="text" className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2.5 px-4 border" placeholder="fas fa-print" />
                          <p className="text-xs text-gray-500 mt-1">Use FontAwesome classes (e.g. 'fas fa-print')</p>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Category Image</label>
                      <div className="flex items-center space-x-4">
                        {categoryForm.image && (
                          <img src={getImageUrl(categoryForm.image)} alt="Preview" className="h-20 w-20 object-contain rounded bg-gray-50 border border-gray-200" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await fetch(`${API_BASE}/upload/images`, {
                                  method: 'POST',
                                  body: formData
                                });
                                const data = await res.json();
                                if (data.url) {
                                  setCategoryForm({ ...categoryForm, image: data.url });
                                }
                              } catch (err) {
                                console.error("Upload failed", err);
                                alert("Image upload failed");
                              }
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-gray-100">
                      <button type="button" onClick={() => { setShowCategoryForm(false); setEditingId(null); }} className="mr-3 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
                      <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shadow-sm">
                        {loading ? 'Saving...' : 'Save Category'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )
          }

          {/* ENQUIRIES VIEW */}
          {
            activeSection === 'enquiries' && (
              <div>
                <h2 className="text-2xl font-bold mb-6">Enquiries</h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="p-4 font-semibold text-gray-600">Date</th>
                          <th className="p-4 font-semibold text-gray-600">Name</th>
                          <th className="p-4 font-semibold text-gray-600">Details</th>
                          <th className="p-4 font-semibold text-gray-600">Type</th>
                          <th className="p-4 font-semibold text-gray-600">Status</th>
                          <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {enquiries.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="p-8 text-center text-gray-500">No enquiries found.</td>
                          </tr>
                        ) : (
                          enquiries.map((enquiry) => (
                            <tr key={enquiry._id} className="hover:bg-gray-50 transition-colors">
                              <td className="p-4 text-sm text-gray-500 whitespace-nowrap">
                                {new Date(enquiry.createdAt).toLocaleDateString()}
                              </td>
                              <td className="p-4">
                                <div className="font-medium text-gray-900">{enquiry.name}</div>
                                <div className="text-sm text-gray-500">{enquiry.email}</div>
                                <div className="text-sm text-gray-500">{enquiry.phone}</div>
                              </td>
                              <td className="p-4">
                                <div className="text-sm text-gray-900 max-w-xs truncate" title={enquiry.message}>{enquiry.message}</div>
                                {enquiry.product && (
                                  <div className="text-xs text-blue-600 mt-1">
                                    <i className="fas fa-box mr-1"></i>
                                    {enquiry.product.name} ({enquiry.product.sku})
                                  </div>
                                )}
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${enquiry.type === 'quote' ? 'bg-purple-100 text-purple-700' :
                                  enquiry.type === 'product' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                  {enquiry.type?.toUpperCase()}
                                </span>
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${enquiry.status === 'new' ? 'bg-green-100 text-green-700' :
                                  enquiry.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                  {enquiry.status?.toUpperCase()}
                                </span>
                              </td>
                              <td className="p-4 text-right">
                                <button
                                  onClick={() => handleEnquiryView(enquiry)}
                                  className="text-blue-600 hover:text-blue-800 p-2"
                                  title="View Details"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )
          }

          {/* USERS VIEW */}
          {
            activeSection === 'users' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">User Management</h2>
                  <button
                    onClick={() => setShowUserForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
                  >
                    <i className="fas fa-plus mr-2"></i> Add User
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <th className="p-4 font-semibold text-gray-600">Name</th>
                          <th className="p-4 font-semibold text-gray-600">Email</th>
                          <th className="p-4 font-semibold text-gray-600">Role</th>
                          <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {users.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="p-8 text-center text-gray-500">No users found.</td>
                          </tr>
                        ) : (
                          users.map((u) => (
                            <tr key={u._id} className="hover:bg-gray-50 transition-colors">
                              <td className="p-4 font-medium text-gray-900">
                                {u.name}
                                {u._id === user?.id && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">You</span>}
                              </td>
                              <td className="p-4 text-gray-500">{u.email}</td>
                              <td className="p-4">
                                <select
                                  value={u.role}
                                  onChange={(e) => handleRoleUpdate(u._id, e.target.value)}
                                  disabled={u._id === user?.id}
                                  className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="viewer">Viewer</option>
                                  <option value="editor">Editor</option>
                                  <option value="admin">Admin</option>
                                </select>
                              </td>
                              <td className="p-4 text-right">
                                <button
                                  onClick={() => handleUserDelete(u._id)}
                                  disabled={u._id === user?.id}
                                  className={`text-red-600 hover:text-red-800 p-2 ${u._id === user?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Delete User"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* User Form Modal */}
                {showUserForm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold">Add New User</h3>
                        <button onClick={() => setShowUserForm(false)} className="text-gray-500 hover:text-gray-700">
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                      <form onSubmit={handleUserSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            required
                            value={userForm.name}
                            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            required
                            value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                          <input
                            type="password"
                            required
                            minLength="6"
                            value={userForm.password}
                            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                          <select
                            value={userForm.role}
                            onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                          <button
                            type="button"
                            onClick={() => setShowUserForm(false)}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            {loading ? 'Creating...' : 'Create User'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Enquiry Detail Modal */}
                {showEnquiryModal && selectedEnquiry && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white">
                        <h3 className="text-xl font-bold text-gray-900">Enquiry Details</h3>
                        <button onClick={() => setShowEnquiryModal(false)} className="text-gray-400 hover:text-gray-600">
                          <i className="fas fa-times text-xl"></i>
                        </button>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer Name</label>
                            <div className="text-gray-900 font-medium">{selectedEnquiry.name}</div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                            <div className="text-gray-900">{new Date(selectedEnquiry.createdAt).toLocaleString()}</div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                            <a href={`mailto:${selectedEnquiry.email}`} className="text-blue-600 hover:underline">{selectedEnquiry.email}</a>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone</label>
                            <a href={`tel:${selectedEnquiry.phone}`} className="text-blue-600 hover:underline">{selectedEnquiry.phone}</a>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Company</label>
                            <div className="text-gray-900">{selectedEnquiry.company || 'Not Provided'}</div>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Enquiry Type</label>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${selectedEnquiry.type === 'quote' ? 'bg-purple-100 text-purple-700' :
                              selectedEnquiry.type === 'product' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                              {selectedEnquiry.type?.toUpperCase()}
                            </span>
                          </div>
                        </div>

                        {selectedEnquiry.product && (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                            <label className="block text-xs font-bold text-blue-800 uppercase mb-2">Interested Product</label>
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 bg-white rounded border border-blue-100 flex items-center justify-center overflow-hidden">
                                {selectedEnquiry.product.images?.[0] ? (
                                  <img src={getImageUrl(selectedEnquiry.product.images[0])} alt="" className="w-full h-full object-contain" />
                                ) : (
                                  <i className="fas fa-box text-blue-200 text-2xl"></i>
                                )}
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">{selectedEnquiry.product.name}</div>
                                <div className="text-sm text-gray-600">SKU: {selectedEnquiry.product.sku}</div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Message</label>
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-800 whitespace-pre-wrap leading-relaxed border border-gray-100 italic">
                            "{selectedEnquiry.message}"
                          </div>
                        </div>
                      </div>
                      <div className="p-6 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={() => setShowEnquiryModal(false)}
                          className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </div >
      </main >
    </div >
  );
};

export default Admin;

